import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Sparkles } from 'lucide-react'

import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { SIGNAL_TYPE_ICONS } from 'components/RecommendationsCard/constants'

const AI_AGENTS = [
  {
    value: 'unreplied_email',
    Icon: SIGNAL_TYPE_ICONS.unreplied_email,
    name: 'Unreplied Email',
    description:
      'Detects inbound emails that haven\u2019t been replied to within 3\u201360 days and recommends follow-up actions with context from the conversation thread.',
  },
  {
    value: 'inactive_deal',
    Icon: SIGNAL_TYPE_ICONS.inactive_deal,
    name: 'Inactive Deal',
    description:
      'Flags deals with no activity for 7+ days or stuck in the same stage for 14+ days, and suggests re-engagement strategies based on deal history.',
  },
  {
    value: 'email_stage_change',
    Icon: SIGNAL_TYPE_ICONS.email_stage_change,
    name: 'Update Deal Stage',
    description:
      'Detects deals where a recent inbound email suggests the deal is ready to advance to the next stage, and provides a one-click apply button to execute the stage change.',
  },
]

const FILTER_LEVELS = [
  {
    value: 'turn_off',
    label: 'Turn Off',
    description: 'No recommendations generated',
  },
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Only high-confidence, clearly actionable items',
  },
  {
    value: 'normal',
    label: 'Normal',
    description: 'Balanced filtering (recommended)',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    description: 'Show almost everything',
  },
  {
    value: 'allow_all',
    label: 'Allow All',
    description:
      'No AI filtering — every detected signal becomes a recommendation',
  },
] as const

const LLM_CONTEXT_MAX_LENGTH = 2000
const LLM_CONTEXT_PLACEHOLDER = `Example: We sell plant-based proteins to K-12 districts and universities. Minimum order is 500 cases. Our primary distributors are Sysco and US Foods. Sales cycle is typically 3-6 months for new accounts.`

export function AISettingsPage() {
  const api = useApiService()
  const { data: companyData, isLoading, refetch } = api.useGetCompanyDetails()

  if (isLoading || !companyData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-fg-secondary text-sm">Loading...</p>
      </div>
    )
  }

  return (
    <AISettingsForm
      key={companyData.id}
      initialFilterLevel={companyData.signal_filter_level ?? 'normal'}
      initialLlmContext={companyData.llm_context ?? ''}
      initialEnabledSignalTypes={
        companyData.enabled_signal_types ?? AI_AGENTS.map((a) => a.value)
      }
      onSaveFilterLevel={async (filterLevel) => {
        await api.updateCompanyDetails({ signalFilterLevel: filterLevel })
        await refetch()
      }}
      onSaveLlmContext={async (llmContext) => {
        await api.updateCompanyDetails({ llmContext })
        await refetch()
      }}
      onSaveEnabledSignalTypes={async (types) => {
        await api.updateCompanyDetails({ enabledSignalTypes: types })
        await refetch()
      }}
    />
  )
}

function AISettingsForm({
  initialFilterLevel,
  initialLlmContext,
  initialEnabledSignalTypes,
  onSaveFilterLevel,
  onSaveLlmContext,
  onSaveEnabledSignalTypes,
}: {
  initialFilterLevel: string
  initialLlmContext: string
  initialEnabledSignalTypes: string[]
  onSaveFilterLevel: (filterLevel: string) => Promise<void>
  onSaveLlmContext: (llmContext: string) => Promise<void>
  onSaveEnabledSignalTypes: (types: string[]) => Promise<void>
}) {
  const [filterLevel, setFilterLevel] = useState(initialFilterLevel)
  const [llmContext, setLlmContext] = useState(initialLlmContext)
  const [enabledTypes, setEnabledTypes] = useState<string[]>(
    initialEnabledSignalTypes
  )
  const [savingField, setSavingField] = useState<string | null>(null)
  const savedContextRef = useRef(initialLlmContext)

  const handleToggleSignalType = useCallback(
    async (signalType: string) => {
      const newTypes = enabledTypes.includes(signalType)
        ? enabledTypes.filter((t) => t !== signalType)
        : [...enabledTypes, signalType]
      setEnabledTypes(newTypes)
      setSavingField(`toggle_${signalType}`)
      try {
        await onSaveEnabledSignalTypes(newTypes)
      } catch {
        toast.error('Failed to update agent setting')
        setEnabledTypes(initialEnabledSignalTypes)
      } finally {
        setSavingField(null)
      }
    },
    [enabledTypes, onSaveEnabledSignalTypes, initialEnabledSignalTypes]
  )

  const handleFilterLevelChange = useCallback(
    async (value: string) => {
      setFilterLevel(value)
      setSavingField('filter_level')
      try {
        await onSaveFilterLevel(value)
      } catch {
        toast.error('Failed to update filter level')
        setFilterLevel(initialFilterLevel)
      } finally {
        setSavingField(null)
      }
    },
    [onSaveFilterLevel, initialFilterLevel]
  )

  const handleContextBlur = useCallback(async () => {
    if (llmContext === savedContextRef.current) return
    setSavingField('llm_context')
    try {
      await onSaveLlmContext(llmContext)
      savedContextRef.current = llmContext
    } catch {
      toast.error('Failed to save business context')
    } finally {
      setSavingField(null)
    }
  }, [llmContext, onSaveLlmContext])

  return (
    <div className="w-full h-full flex flex-1 min-h-0">
      {/* Left sidebar — gray background like deals detail page */}
      <div className="w-[27rem] shrink-0 border-r border-line bg-surface-alt flex flex-col overflow-y-auto">
        <div className="px-5 py-4">
          <h1 className="text-xl font-bold text-fg-dark inline-flex items-center gap-1.5">
            <Sparkles size={18} />
            AI Agents
          </h1>
        </div>
        <div className="px-5 pb-5 space-y-3">
          {AI_AGENTS.map((agent) => {
            const isEnabled = enabledTypes.includes(agent.value)
            const isSaving = savingField === `toggle_${agent.value}`
            return (
              <div
                key={agent.value}
                className={cn(
                  'rounded-lg border bg-surface p-4 transition-opacity',
                  isEnabled ? 'border-line' : 'border-line opacity-60'
                )}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <agent.Icon size={18} className="text-brand" />
                    <span className="text-sm font-medium text-fg">
                      {agent.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isEnabled}
                    disabled={isSaving}
                    onClick={() => handleToggleSignalType(agent.value)}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors',
                      isEnabled ? 'bg-brand' : 'bg-surface-disabled',
                      isSaving && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow-sm transition-transform mt-0.5',
                        isEnabled
                          ? 'translate-x-4 ml-0.5'
                          : 'translate-x-0 ml-0.5'
                      )}
                    />
                  </button>
                </div>
                <p className="text-xs text-fg-secondary leading-relaxed">
                  {agent.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Settings — right side, white background fills full width */}
      <div className="flex-1 overflow-y-auto bg-surface">
        <div className="px-[42px] pb-6 pt-[4.5rem] max-w-2xl space-y-8">
          {/* Company Context */}
          <section>
            <h3 className="text-base font-semibold text-fg mb-1">
              About Your Business
            </h3>
            <p className="text-sm text-fg-secondary mb-4">
              Help our AI Agents understand your business so recommendations are
              more targeted and relevant. Describe your products, target market,
              distributors, and typical sales cycle.
            </p>
            <textarea
              value={llmContext}
              onChange={(e) => {
                if (e.target.value.length <= LLM_CONTEXT_MAX_LENGTH) {
                  setLlmContext(e.target.value)
                }
              }}
              onBlur={handleContextBlur}
              placeholder={LLM_CONTEXT_PLACEHOLDER}
              rows={5}
              className="w-full rounded-lg border border-line bg-surface p-3 text-sm text-fg placeholder:text-fg-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand resize-y"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-fg-tertiary">
                {savingField === 'llm_context' ? 'Saving...' : '\u00A0'}
              </p>
              <p className="text-xs text-fg-tertiary">
                {llmContext.length}/{LLM_CONTEXT_MAX_LENGTH}
              </p>
            </div>
          </section>

          {/* Signal Filter Level */}
          <section>
            <h3 className="text-base font-semibold text-fg mb-1">
              Recommendation Sensitivity
            </h3>
            <p className="text-sm text-fg-secondary mb-4">
              Controls how many AI-generated recommendations surface. More
              aggressive filtering means fewer but higher-confidence
              recommendations.
            </p>
            <div className="space-y-2">
              {FILTER_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    filterLevel === level.value
                      ? 'border-brand bg-surface-hover'
                      : 'border-line hover:bg-surface-hover',
                    savingField === 'filter_level' &&
                      'opacity-50 pointer-events-none'
                  )}
                >
                  <input
                    type="radio"
                    name="filter_level"
                    value={level.value}
                    checked={filterLevel === level.value}
                    onChange={() => handleFilterLevelChange(level.value)}
                    className="mt-0.5 accent-brand"
                  />
                  <div>
                    <span className="text-sm font-medium text-fg">
                      {level.label}
                    </span>
                    <p className="text-xs text-fg-secondary mt-0.5">
                      {level.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
